import { Children, isValidElement, useState } from "react";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { Link } from "react-router-dom";
import { FaEllipsisVertical } from "react-icons/fa6";
import { gold, ink, muted } from "./tableStyles";

export function collectActions(node, inheritedLabel = "") {
    const items = [];

    Children.forEach(node, (child) => {
        if (child == null || child === false) return;
        if (!isValidElement(child)) return;

        const nextLabel = typeof child.props.title === "string" ? child.props.title : inheritedLabel;
        const isIconButton = child.type === IconButton;

        if (isIconButton) {
            const { to, href, onClick, color, disabled, target, component, LinkComponent, children } = child.props;
            const hasAction = Boolean(to || href || onClick);
            if (!hasAction) return;

            items.push({
                label: nextLabel || "Ação",
                icon: children,
                to,
                href,
                target,
                onClick,
                color,
                disabled,
                linkComponent: component === Link || LinkComponent === Link || to ? Link : component,
            });
            return;
        }

        if (child.props.children) {
            items.push(...collectActions(child.props.children, nextLabel));
        }
    });

    return items;
}

export default function RowActionsMenu({ children }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const items = collectActions(children);

    if (items.length === 0) return null;

    const open = Boolean(anchorEl);

    const handleOpen = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <IconButton
                aria-label="Abrir ações"
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleOpen}
                size="small"
                sx={{
                    color: muted,
                    "&:hover": { color: ink, bgcolor: "rgba(200,164,89,0.12)" },
                    "&:focus-visible": { outline: `2px solid ${gold}`, outlineOffset: 2 },
                }}
            >
                <FaEllipsisVertical />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={(event) => event.stopPropagation()}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            mt: 0.5,
                            minWidth: 200,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 8px 24px rgba(11,10,8,0.08)",
                        },
                    },
                }}
            >
                {items.map((item, index) => {
                    const isDelete = item.color === "error";
                    const shared = {
                        key: `${item.label}-${index}`,
                        disabled: item.disabled,
                        onClick: (event) => {
                            item.onClick?.(event);
                            handleClose();
                        },
                        sx: {
                            py: 1,
                            color: isDelete ? "error.main" : ink,
                            "& .MuiListItemIcon-root": {
                                color: isDelete ? "error.main" : muted,
                                minWidth: 32,
                            },
                        },
                    };

                    if (item.to) {
                        return (
                            <MenuItem {...shared} component={item.linkComponent || Link} to={item.to}>
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.label} />
                            </MenuItem>
                        );
                    }

                    if (item.href) {
                        return (
                            <MenuItem
                                {...shared}
                                component="a"
                                href={item.href}
                                target={item.target}
                                rel={item.target === "_blank" ? "noreferrer" : undefined}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.label} />
                            </MenuItem>
                        );
                    }

                    return (
                        <MenuItem {...shared}>
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}
